/* eslint-disable @typescript-eslint/naming-convention */
import { lazy, useContext, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { Box, Typography } from '@astro-ui/components'
import { getFeatureFlagByLocationID } from 'utils/api'
import storeContext from 'storeContext'
import type { AuthInitialStateType } from 'features/Auth/redux/Auth/slice'
import {
  canBypassPBV1Block,
  getInitialOngoingRoleLocation,
  getPBV2RedirectPath,
} from 'features/Order/utils/ongoingOrders'
import ForbiddenImage from 'assets/images/astronauts.svg'

const NewOrder = lazy(() => import('../pages/OngoingOrders/NewOrders'))
const BackToHub = lazy(() => import('../pages/OngoingOrders/BackToHubOrders'))
const UpcomingOrders = lazy(() => import('../pages/OngoingOrders/UpcomingOrders'))
const Picking = lazy(() => import('../pages/OngoingOrders/PickingOrders'))
const Packing = lazy(() => import('../pages/OngoingOrders/PackingOrders'))
const OnDelivery = lazy(() => import('../pages/OngoingOrders/OnDeliveryOrders'))
const ReadyForDelivery = lazy(() => import('../pages/OngoingOrders/ReadyForDelivery'))
const WaitingConfirmation = lazy(() => import('../pages/OngoingOrders/WaitingConfirmation'))

type PBV1FeatureKeyType = 'force_pbv2' | 'migrate_pesanan_berlangsung'

const getPBV1FeatureFlag = async (locationId: string, featureKey: PBV1FeatureKeyType) => {
  try {
    const response = await getFeatureFlagByLocationID(locationId, { featureKey })
    return response?.data?.data?.features?.[featureKey] === true
  } catch {
    return null
  }
}

const ForbiddenState = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    style={{ minHeight: 'calc(100vh - 200px)' }}
    gap="24px"
  >
    <img src={ForbiddenImage} alt="forbidden" style={{ width: 320 }} />
    <Typography fontSize={16} fontWeight={700} color="#6B7280">
      Lokasi ini sudah menggunakan Pesanan Berlangsung V2
    </Typography>
  </Box>
)

const DynamicRouteHandler = () => {
  const { status } = useParams<{ status: string }>()
  const history = useHistory()
  const location = useLocation()
  const { state: authState } = useContext(storeContext.AuthContext) as unknown as {
    state: AuthInitialStateType
  }
  const [isAllowed, setIsAllowed] = useState<boolean | null>(null)
  const searchParams = new URLSearchParams(location.search)
  const requestedLocationId = searchParams.get('locationId')
  const initialLocation = useMemo(
    () => getInitialOngoingRoleLocation(authState.userData, requestedLocationId),
    [authState.userData, requestedLocationId],
  )
  const locationId = requestedLocationId ?? initialLocation?.location_id?.toString() ?? ''
  const bypassesPBV1Block = canBypassPBV1Block(authState.userData, locationId)

  useEffect(() => {
    let isCurrent = true

    const checkPBV1Access = async () => {
      if (!requestedLocationId && initialLocation) {
        const nextSearchParams = new URLSearchParams(location.search)
        nextSearchParams.set('locationId', String(initialLocation.location_id))
        history.replace({ pathname: location.pathname, search: nextSearchParams.toString() })
        return
      }

      if (!locationId) {
        setIsAllowed(true)
        return
      }

      setIsAllowed(null)

      const forcePBV2 = await getPBV1FeatureFlag(locationId, 'force_pbv2')
      if (!isCurrent) return
      if (forcePBV2) {
        history.replace({
          pathname: getPBV2RedirectPath(status),
          search: location.search,
        })
        return
      }

      if (bypassesPBV1Block) {
        setIsAllowed(true)
        return
      }

      const usesPBV2 = await getPBV1FeatureFlag(locationId, 'migrate_pesanan_berlangsung')
      if (isCurrent) setIsAllowed(usesPBV2 !== true)
    }

    checkPBV1Access()
    return () => {
      isCurrent = false
    }
  }, [
    bypassesPBV1Block,
    history,
    initialLocation,
    location.pathname,
    location.search,
    locationId,
    requestedLocationId,
    status,
  ])

  if (isAllowed === null) return null
  if (!isAllowed) return <ForbiddenState />

  const componentMapping: {
    [key: string]:
      | React.LazyExoticComponent<() => JSX.Element>
      | React.LazyExoticComponent<React.MemoExoticComponent<() => JSX.Element>>
  } = {
    'back-to-hub': BackToHub,
    'new-order': NewOrder,
    'upcoming-order': UpcomingOrders,
    picking: Picking,
    packing: Packing,
    'on-delivery': OnDelivery,
    'ready-for-delivery': ReadyForDelivery,
    'waiting-confirmation': WaitingConfirmation,
  }
  const Component = componentMapping[status]
  if (!Component) return <NewOrder />
  return <Component />
}
export default DynamicRouteHandler
